import { createReducer, on } from '@ngrx/store';
import { RolePermissionActions } from './role-permission.actions';
import { AllModules, AssignedRoleCountResponse, FilterParams, RoleNames, RolePermission, SortParams } from '../../models/rolePermission.model';
import { AuthActions } from '../auth/auth.actions';

export const ROLE_PERMISSION_FEATURE_KEY = 'rolePermission';

export interface RolePermissionState {
  rolePermission: RolePermission | null;
  roleList: RolePermission[];
  systemRolelist: RolePermission[];
  roleNames: RoleNames[];
  allModulePermissions: AllModules[];
  role: RolePermission | null;
  total: number;
  page: number;
  filter: FilterParams;
  sort: SortParams;
  currentPage: number;
  assignedRoleData: AssignedRoleCountResponse | null;
  limit: number;
  loading: boolean;
  error: string | null;
}

export const initialState: RolePermissionState = {
  rolePermission: null,
  roleList: [],
  systemRolelist: [],
  roleNames: [],
  allModulePermissions: [],
  role: null,
  total: 0,
  page: 1,
  filter: {},
  sort: {
    key: 'createdAt',
    order: 'desc',
  },
  currentPage: 1,
  assignedRoleData: null,
  limit: 10,
  loading: false,
  error: null,
};

export const rolePermissionReducer = createReducer(
  initialState,
  on(RolePermissionActions.loadRolePermission, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(RolePermissionActions.loadRolePermissionSuccess, (state, { rolePermission }) => ({
    ...state,
    rolePermission,
    loading: false,
    error: null,
  })),
  on(RolePermissionActions.loadRolePermissionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  // Create Reducers
  on(RolePermissionActions.createRolePermission, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(RolePermissionActions.createRolePermissionSuccess, (state, { role }) => ({
    ...state,
    // rolePermission: role,
    roleList: [...state.roleList, role], // Add new role to the list
    total: state.total + 1,   // Increment total count
    loading: false,
  })),
  on(RolePermissionActions.createRolePermissionFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),

  // List Roles
  on(RolePermissionActions.listRolePermissions, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(RolePermissionActions.listRolePermissionsSuccess, (state, { response }) => ({
    ...state,
    roleList: response.data.roles.list,
    total: response.data.roles.total,
    currentPage: response.data.roles.page,
    limit: response.data.roles.limit,
    loading: false,
  })),
  on(RolePermissionActions.listRolePermissionsFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),


  // Role Name list
  on(RolePermissionActions.roleNameList, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(RolePermissionActions.roleNameListSuccess, (state, { response }) => ({
    ...state,
    roleNames: response.data,
    loading: false,
    error: null,
  })),
  on(RolePermissionActions.roleNameListFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),

  // All Module Permissions
  on(RolePermissionActions.allModulePermissions, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(RolePermissionActions.allModulePermissionsSuccess, (state, { response }) => ({
    ...state,
    allModulePermissions: response.data,
    loading: false,
    error: null,
  })),
  on(RolePermissionActions.allModulePermissionsFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),

  // System Role Permissions
  on(RolePermissionActions.listSystemRolePermissions, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(RolePermissionActions.listSystemRolePermissionsSuccess, (state, { response }) => ({
    ...state,
    systemRolelist: response.data,
    loading: false,
  })),
  on(RolePermissionActions.listSystemRolePermissionsFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),

  // For single role
  on(RolePermissionActions.selectCurrentRolePermission, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(RolePermissionActions.selectCurrentRolePermissionSuccess, (state, { role }) => ({
    ...state,
    role: role,
    loading: false,
    error: null,
  })),
  on(RolePermissionActions.selectCurrentRolePermissionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Clear current role
  on(RolePermissionActions.clearCurrentRolePermission, (state) => ({
    ...state,
    role: null,
    loading: false,
    error: null,
  })),

  // Update Role
  on(RolePermissionActions.updateRolePermission, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(RolePermissionActions.updateRolePermissionSuccess, (state, { role }) => ({
    ...state,
    // rolePermission: role,
    roleList: state.roleList.map(r =>
      r._id === role._id ? role : r
    ),
    loading: false,
  })),

  on(RolePermissionActions.updateRolePermissionFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),

  // Delete Role
  on(RolePermissionActions.deleteRolePermission, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(RolePermissionActions.deleteRolePermissionSuccess, (state, { id }) => ({
    ...state,
    roleList: state.roleList.filter(role => role._id !== id),
    total: state.total - 1,
    loading: false,
  })),

  on(RolePermissionActions.deleteRolePermissionFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),
  on(RolePermissionActions.setRoleQueryParams, (state, { params, body }) => ({
    ...state,
      page: params.page ?? state.page,
      limit: params.limit ?? state.limit,
      filter: body?.filter ?? state.filter,
      sort: body?.sort ?? state.sort,
  })),

  // Check Assigned Role Count
  on(RolePermissionActions.checkAssignedRoleCount, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(RolePermissionActions.checkAssignedRoleCountSuccess, (state, { response }) => ({
    ...state,
    assignedRoleData: response,
    loading: false,
  })),
  on(RolePermissionActions.checkAssignedRoleCountFailure, (state, { error }) => ({
    ...state,
    error,
    loading: false,
  })),
  // Reset state on logout
  on(AuthActions.logout, () => initialState)
);
